import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Search, X, Folder, ClipboardList, CheckCircle2, Loader2, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getMediaDetailPath } from '@/components/ui/PhotoGallery';
import { MobileHeaderSearch } from '@/pages/_partials/mobile-headers';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { useApiCache } from '@/lib/contexts/api-cache-context';
import { useTranslation } from 'react-i18next';

// Fonction de formatage de date selon le design Figma : "Complété le 15/01/2024 à 14:30"
function formatCompletedDate(dateString) {
    if (!dateString) return null;
    try {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `Complété le ${day}/${month}/${year} à ${hours}:${minutes}`;
    } catch {
        return null;
    }
}

// Fonction debounce simple
function debounce(fn, delay) {
    let timeoutId;
    return (...args) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(() => fn(...args), delay);
    };
}

export default function SearchPage() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const { getCache, setCache, clearReportReturnTo } = useApiCache();

    const handleClose = useCallback(() => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/dashboard', { replace: true });
        }
    }, [navigate, location.key]);

    // Réinitialiser le returnTo des rapports quand on arrive sur la page Recherche
    useEffect(() => {
        clearReportReturnTo();
    }, [clearReportReturnTo]);

    // Initialiser la recherche depuis l'URL si présente
    const initialQuery = searchParams.get('q') || '';
    const [searchQuery, setSearchQuery] = useState(initialQuery);
    const [searchResults, setSearchResults] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [sectionErrors, setSectionErrors] = useState({
        folders: null,
        photos: null,
        templates: null,
        reports: null,
    });

    // Fonction de recherche (extraite pour réutilisation)
    const performSearch = useCallback(
        async (query) => {
            if (query.trim().length < 2) {
                setSearchResults(null);
                setIsLoading(false);
                return;
            }

            // Vérifier le cache d'abord
            const cacheKey = `search:${query.trim().toLowerCase()}`;
            const cached = getCache(cacheKey, 'default');
            if (cached) {
                setSearchResults(cached.results);
                setSectionErrors(cached.errors);
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            try {
                const results = await horodatyApi.globalSearch(query, 5);
                const formattedResults = {
                    folders: results.folders || [],
                    photos: results.pictures || [],
                    templates: results.templates || [],
                    reports: results.reports || [],
                };
                const formattedErrors = {
                    folders: results.foldersError,
                    photos: results.picturesError,
                    templates: results.templatesError,
                    reports: results.reportsError,
                };
                setSearchResults(formattedResults);
                setSectionErrors(formattedErrors);

                // Mettre en cache les résultats
                setCache(cacheKey, { results: formattedResults, errors: formattedErrors }, 'default');
            } catch (err) {
                console.error('Erreur recherche:', err);
                setSearchResults({
                    folders: [],
                    photos: [],
                    templates: [],
                    reports: [],
                });
                const sectionError = t('common:apiErrors.generic');
                setSectionErrors({
                    folders: sectionError,
                    photos: sectionError,
                    templates: sectionError,
                    reports: sectionError,
                });
            } finally {
                setIsLoading(false);
            }
        },
        [getCache, setCache, t],
    );

    // Recherche initiale si query présente dans l'URL
    useEffect(() => {
        if (initialQuery.length >= 2) {
            performSearch(initialQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Uniquement au montage

    // Recherche avec debounce
    const debouncedSearch = useRef(
        debounce(async (query) => {
            await performSearch(query);
        }, 300),
    ).current;

    // Debounce pour mise à jour de l'URL (séparé pour éviter trop de pushState)
    const debouncedUpdateUrl = useRef(
        debounce((query) => {
            if (query.trim().length >= 2) {
                setSearchParams({ q: query }, { replace: true });
            } else {
                setSearchParams({}, { replace: true });
            }
        }, 500),
    ).current;

    const handleSearch = useCallback(
        (value) => {
            setSearchQuery(value);
            if (value.trim().length >= 2) {
                setIsLoading(true);
            } else {
                setSearchResults(null);
                setSectionErrors({
                    folders: null,
                    photos: null,
                    templates: null,
                    reports: null,
                });
            }
            debouncedSearch(value);
            debouncedUpdateUrl(value);
        },
        [debouncedSearch, debouncedUpdateUrl],
    );

    const handleClear = useCallback(() => {
        setSearchQuery('');
        setSearchResults(null);
        setSectionErrors({
            folders: null,
            photos: null,
            templates: null,
            reports: null,
        });
        setSearchParams({}, { replace: true });
    }, [setSearchParams]);

    // Navigation

    // Navigation handlers
    const searchFrom = `/search${searchQuery ? `?q=${encodeURIComponent(searchQuery)}` : ''}`;

    const goToFolder = (folder) => navigate(`/folders/${folder.uniq_id}`, { state: { from: searchFrom } });

    const goToPhoto = (photo) => {
        // URL canonique: /pictures/{id} pour image, /videos/{id} pour vidéo
        // Les IDs du dossier et de l'intervention sont résolus via l'API getPicture
        const mediaPath = getMediaDetailPath(photo);
        if (mediaPath) {
            navigate(mediaPath, { state: { from: searchFrom } });
        } else if (photo.intervention_uniq_id) {
            navigate(`/folders/${photo.intervention_uniq_id}`, { state: { from: searchFrom } });
        } else if (photo.folder_uniq_id) {
            navigate(`/folders/${photo.folder_uniq_id}`, { state: { from: searchFrom } });
        }
    };

    const goToTemplate = (template) => navigate(`/procedures/${template.uniq_id}`, { state: { from: searchFrom } });

    const goToReport = (report) => {
        if (report.template?.uniq_id && report.intervention?.uniq_id) {
            const returnTo = location.pathname + location.search;
            navigate(`/procedures/${report.template.uniq_id}/reports/${report.intervention.uniq_id}`, {
                state: { returnTo },
            });
        }
    };

    // Calculer si on a des résultats ou des erreurs à afficher
    const hasResultsOrErrors =
        searchResults ||
        sectionErrors.folders ||
        sectionErrors.photos ||
        sectionErrors.templates ||
        sectionErrors.reports;

    const hasResults =
        searchResults &&
        (searchResults.folders.length > 0 ||
            searchResults.photos.length > 0 ||
            searchResults.templates.length > 0 ||
            searchResults.reports.length > 0);

    // Composant pour afficher une erreur de section
    const SectionError = ({ error, sectionName }) => {
        if (!error) return null;
        return (
            <div className="mb-6">
                <h3 className="text-sm text-muted-foreground mb-3">{sectionName}</h3>
                <div className="bg-destructive/10 dark:bg-destructive/20 rounded-2xl p-4 text-sm text-destructive">
                    {error}
                </div>
            </div>
        );
    };

    return (
        <div className="h-screen flex flex-col bg-background">
            {/* Mobile Search Header */}
            <MobileHeaderSearch
                value={searchQuery}
                onChange={handleSearch}
                onClose={handleClose}
                onClear={handleClear}
                placeholder={t('search:rechercher')}
            />

            {/* Desktop Search Bar */}
            <div className="desktop-only hidden md:block bg-surface border-b border-border px-6 py-4">
                <div className="max-w-4xl mx-auto">
                    <div className="relative">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder={t('search:rechercher')}
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            autoFocus
                            className="w-full pl-12 pr-20 py-3 bg-surface dark:bg-surface rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 border border-border"
                        />
                        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                            <Button
                                variant="plain"
                                color="neutral"
                                size="auto"
                                onClick={searchQuery ? handleClear : handleClose}
                                className="h-8 w-8"
                            >
                                <X className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-surface dark:bg-background">
                <div className="max-w-4xl mx-auto px-4 md:px-6 py-6 md:py-8">
                    {/* Loading State */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-16">
                            <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
                            <p className="text-sm text-muted-foreground">{t('search:rechercheEnCours')}</p>
                        </div>
                    )}

                    {/* Empty State - No search yet */}
                    {!searchResults && !isLoading && !hasResultsOrErrors && (
                        <div className="flex flex-col items-center justify-center py-16 md:py-20 text-center">
                            <div className="w-16 h-16 md:w-20 md:h-20 bg-surface-hover dark:bg-surface rounded-full flex items-center justify-center mb-4 md:mb-6">
                                <Search className="w-8 h-8 md:w-10 md:h-10 text-muted-foreground" />
                            </div>
                            <h2 className="text-lg md:text-xl font-semibold text-foreground mb-2">{t('search:rechercher2')}</h2>
                            <p className="text-sm md:text-base text-muted-foreground">
                                {t('search:recherchezDesPhotosDossiersOuProcedures')}
                            </p>
                        </div>
                    )}

                    {/* Results */}
                    {hasResultsOrErrors && !isLoading && (
                        <div className="space-y-6">
                            {/* Rapports de procédures - Error */}
                            <SectionError error={sectionErrors.reports} sectionName="Rapports" />

                            {/* Rapports de procédures */}
                            {searchResults?.reports?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm text-muted-foreground mb-3">
                                        {t('search:rapports')}{searchResults.reports.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {searchResults.reports.map((report) => (
                                            <div
                                                key={report.uniq_id}
                                                onClick={() => goToReport(report)}
                                                className="bg-card rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
                                            >
                                                {/* Icon container */}
                                                <div className="w-[50px] h-[50px] bg-muted dark:bg-surface-hover rounded-xl flex items-center justify-center flex-shrink-0">
                                                    <CheckCircle2 className="h-5 w-5 text-secondary" />
                                                </div>
                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[15px] text-foreground truncate">
                                                            {report.template?.name || t('search:rapport')}
                                                            {report.intervention?.name
                                                                ? ` - ${report.intervention.name}`
                                                                : ''}
                                                        </p>
                                                        <span
                                                            className={`px-2 py-0.5 rounded-md text-[11px] font-medium flex-shrink-0 ${
                                                                report.status === 'completed'
                                                                    ? 'bg-secondary/20 text-secondary'
                                                                    : 'bg-badge-pending/20 text-badge-pending'
                                                            }`}
                                                        >
                                                            {report.status === 'completed' ? t('search:valide') : t('search:brouillon')}
                                                        </span>
                                                    </div>
                                                    <p className="text-[13px] text-muted-foreground truncate">
                                                        {formatCompletedDate(report.updated_at) ||
                                                            report.folder?.name ||
                                                            ''}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Dossiers - Error */}
                            <SectionError error={sectionErrors.folders} sectionName="Dossiers" />

                            {/* Dossiers */}
                            {searchResults?.folders?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm text-muted-foreground mb-3">
                                        {t('search:dossiers')}{searchResults.folders.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {searchResults.folders.map((folder) => (
                                            <div
                                                key={folder.uniq_id}
                                                onClick={() => goToFolder(folder)}
                                                className="bg-card rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
                                            >
                                                {/* Icon container */}
                                                <div className="w-[50px] h-[50px] bg-muted dark:bg-surface-hover rounded-xl flex items-center justify-center flex-shrink-0">
                                                    <Folder className="h-5 w-5 text-muted-foreground" />
                                                </div>
                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[15px] text-foreground truncate">
                                                            {folder.name}
                                                        </p>
                                                    </div>
                                                    <p className="text-[13px] text-muted-foreground truncate">
                                                        {folder.interventions?.length
                                                            ? `${folder.interventions.length} intervention${folder.interventions.length > 1 ? 's' : ''}`
                                                            : [folder.city, folder.cp].filter(Boolean).join(' ') ||
                                                              folder.ref ||
                                                              ''}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Photos - Error */}
                            <SectionError error={sectionErrors.photos} sectionName="Photos" />

                            {/* Photos */}
                            {searchResults?.photos?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm text-muted-foreground mb-3">
                                        {t('search:photos')}{searchResults.photos.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {searchResults.photos.map((photo) => (
                                            <div
                                                key={photo.uniq_id}
                                                onClick={() => goToPhoto(photo)}
                                                className="bg-card rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
                                            >
                                                {/* Thumbnail */}
                                                <div className="w-[50px] h-[50px] rounded-xl overflow-hidden bg-muted dark:bg-surface-hover flex-shrink-0 relative">
                                                    <img
                                                        src={`/api/gateway/api/pictures/${photo.uniq_id}?format=thumbnail&band=false`}
                                                        alt={photo.name || t('search:photo')}
                                                        loading="lazy"
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.style.display = 'none';
                                                            e.target.nextElementSibling?.classList.remove('hidden');
                                                        }}
                                                    />
                                                    <div className="hidden absolute inset-0 flex items-center justify-center">
                                                        <ImageIcon className="w-5 h-5 text-muted-foreground" />
                                                    </div>
                                                </div>
                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <p className="text-[15px] text-foreground truncate">
                                                            {photo.name || photo.file_name || t('search:photo')}
                                                        </p>
                                                    </div>
                                                    <p className="text-[13px] text-muted-foreground truncate">
                                                        {photo.folder_name || photo.intervention_name || ''}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Templates - Error */}
                            <SectionError error={sectionErrors.templates} sectionName="Procédures" />

                            {/* Templates de procédures (optionnel, pas visible dans le Figma) */}
                            {searchResults?.templates?.length > 0 && (
                                <div className="mb-6">
                                    <h3 className="text-sm text-muted-foreground mb-3">
                                        {t('search:procedures')}{searchResults.templates.length})
                                    </h3>
                                    <div className="space-y-3">
                                        {searchResults.templates.map((template) => (
                                            <div
                                                key={template.id || template.uniq_id}
                                                onClick={() => goToTemplate(template)}
                                                className="bg-card rounded-2xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow cursor-pointer"
                                            >
                                                {/* Icon container */}
                                                <div className="w-[50px] h-[50px] bg-muted dark:bg-surface-hover rounded-xl flex items-center justify-center flex-shrink-0">
                                                    <ClipboardList className="h-5 w-5 text-muted-foreground" />
                                                </div>
                                                {/* Content */}
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        {template.color && (
                                                            <div
                                                                className="w-3 h-3 rounded-full shrink-0"
                                                                style={{ backgroundColor: template.color }}
                                                            />
                                                        )}
                                                        <p className="text-[15px] text-foreground truncate">
                                                            {template.name}
                                                        </p>
                                                    </div>
                                                    {template.description && (
                                                        <p className="text-[13px] text-muted-foreground truncate">
                                                            {template.description}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Results */}
                            {!hasResults && (
                                <div className="text-center py-12">
                                    <p className="text-muted-foreground">{t('search:aucunResultatTrouvePour')}{searchQuery}"</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
